import { featureFlagsAtom } from '@polaris/core/stores'
import { shorten } from '@polaris/core/utils'
import { useCookiesNext } from 'cookies-next/client'
import { useAtomValue } from 'jotai'
import { Clock6, Moon, SunDim } from 'lucide-react'
import { motion } from 'motion/react'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { useCallback, useState } from 'react'
import { useMpcBalances } from '@/src/hooks/mpc/use-mpc-balances'
import { useCopyToClipboardWithDelay } from '@/src/hooks/use-copy-to-clipboard-with-delay'
import { useUserSettings } from '@/src/hooks/use-user-settings'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { usePortfolioView } from '@/src/stores/portfolio-drawer/actions'
import { sessionIdAtom } from '@/src/stores/user/atoms'
import { Input } from '../../ui/input'
import {
  ListRow,
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../../ui/list-row'
import {
  RadioButtonGroup,
  RadioButtonGroupItem,
} from '../../ui/radio-button-group'
import { Switch } from '../../ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipDescription,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip'

export function SettingsScreen() {
  const { getCookie, setCookie } = useCookiesNext()
  const trackingConsent = getCookie('tracking_consent') === 'true'
  const { updateView } = usePortfolioView()
  const { ignoredGroupedAccounts } = useAtomValue(walletAccountsAtom)
  const featureFlags = useAtomValue(featureFlagsAtom)
  const { balancesByChain } = useMpcBalances()
  const sessionId = useAtomValue(sessionIdAtom)

  const { userSettings, updateSettings, onSmallestBalanceInput } =
    useUserSettings()
  const [smallestBalanceUsd, setSmallestBalanceUsd] = useState(
    userSettings.smallestBalanceUsd.toString(),
  )

  const { mutate: copyToClipboard, isHolding: isCopied } =
    useCopyToClipboardWithDelay()

  const autopilotSettingsDisabled = balancesByChain.length === 0

  // Theme state from store
  const { theme, setTheme } = useTheme()

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value.trim()
      const cleanedInput = onSmallestBalanceInput(input)
      if (cleanedInput !== null) {
        setSmallestBalanceUsd(cleanedInput)
      }
    },
    [onSmallestBalanceInput],
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col'
    >
      <section className='flex flex-col pb-6'>
        <h6 className='text-secondary-100 pb-3 text-sm'>General</h6>
        <div className='-mx-4'>
          {/* Not implemented yet */}
          {/* <ListRowButton size='md' className='py-3'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Language</ListRowSideName>
            </ListRowSide>
            <div className='flex items-center gap-3'>
              <span className='text-secondary'>English</span>
              <ChevronRight size={24} className='text-tertiary-100' />
            </div>
          </ListRowButton>
          <ListRowButton size='md' className='py-3'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Currency</ListRowSideName>
            </ListRowSide>
            <div className='flex items-center gap-3'>
              <span className='text-secondary'>USD</span>
              <ChevronRight size={24} className='text-tertiary-100' />
            </div>
          </ListRowButton> */}
          <ListRow size='md' className='py-2'>
            <ListRowSide>
              <ListRowSideName>Analytics</ListRowSideName>
              <ListRowSideDescription className='max-w-[268px] leading-6'>
                Help Polaris improve its products and services by allowing
                analytics of usage data.
              </ListRowSideDescription>
            </ListRowSide>
            <Switch
              size='large'
              checked={trackingConsent}
              onCheckedChange={(value) => {
                setCookie('tracking_consent', value ? 'true' : 'false', {
                  maxAge: 31536000,
                })
              }}
            />
          </ListRow>
        </div>
      </section>
      <section className='flex flex-col pb-6'>
        <h6 className='text-secondary-100 pb-3 text-sm'>Display</h6>
        <div className='-mx-4'>
          {featureFlags.lightMode && (
            <ListRow size='md' className='py-2'>
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Appearance</ListRowSideName>
              </ListRowSide>
              <RadioButtonGroup
                onValueChange={(e) =>
                  setTheme(e as 'light' | 'dark' | 'system')
                }
                value={theme}
                defaultValue='system'
              >
                <RadioButtonGroupItem className='px-1' value='light'>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SunDim width={24} height={24} />
                      </TooltipTrigger>
                      <TooltipContent
                        className='max-w-[280px] bg-surface1-100'
                        side='top'
                        hideArrow
                      >
                        <p className='text-secondary text-xs'>Light</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </RadioButtonGroupItem>
                <RadioButtonGroupItem className='px-1' value='dark'>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Moon width={24} height={24} />
                      </TooltipTrigger>
                      <TooltipContent
                        className='max-w-[280px] bg-surface1-100'
                        side='top'
                        hideArrow
                      >
                        <p className='text-secondary text-xs'>Dark</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </RadioButtonGroupItem>
                <RadioButtonGroupItem className='px-1' value='system'>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Clock6 width={24} height={24} />
                      </TooltipTrigger>
                      <TooltipContent
                        className='max-w-[280px] bg-surface1-100'
                        side='top'
                        hideArrow
                      >
                        <p className='text-secondary text-xs'>Automatic</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </RadioButtonGroupItem>
              </RadioButtonGroup>
            </ListRow>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <ListRow size='md' className='py-2'>
                  <ListRowSide className='flex-row items-center gap-4'>
                    <ListRowSideName>Mask balances</ListRowSideName>
                  </ListRowSide>
                  <Switch
                    size='large'
                    checked={userSettings.maskBalances}
                    onCheckedChange={(value) => {
                      updateSettings({ maskBalances: value })
                    }}
                  />
                </ListRow>
              </TooltipTrigger>
              <TooltipContent>
                <div className='flex items-center gap-3'>
                  <div className='flex-flex-col gap-1'>
                    <TooltipDescription>
                      Hide balance amounts in your portfolio.
                    </TooltipDescription>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <ListRow size='md' className='py-2'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Hide small balances</ListRowSideName>
            </ListRowSide>
            <Switch
              size='large'
              checked={userSettings.hideSmallBalances}
              onCheckedChange={(value) => {
                updateSettings({
                  hideSmallBalances: value,
                })
              }}
            />
          </ListRow>
          <ListRow size='md' className='py-2'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Hide balances below</ListRowSideName>
            </ListRowSide>
            <Input
              value={smallestBalanceUsd}
              dimension='small'
              type='text'
              containerClassName='max-w-[62px]'
              onChange={handleOnChange}
            />
          </ListRow>
        </div>
      </section>
      <section className='flex flex-col pb-6'>
        <h6 className='text-secondary-100 pb-3 text-sm'>Wallets</h6>
        <div className='-mx-4'>
          {ignoredGroupedAccounts.length > 0 && (
            <ListRowButton
              size='md'
              className='py-3'
              onClick={() => updateView({ view: 'settings-ignored-accounts' })}
            >
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Ignored wallets</ListRowSideName>
              </ListRowSide>
              <ListRowSide showIndicator>
                <ListRowSideDescription>
                  {ignoredGroupedAccounts.length}
                </ListRowSideDescription>
              </ListRowSide>
            </ListRowButton>
          )}
          <ListRowButton
            size='md'
            className='py-3'
            onClick={() => updateView({ view: 'settings-autopilot' })}
            disabled={autopilotSettingsDisabled}
          >
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Autopilot settings</ListRowSideName>
            </ListRowSide>
            <ListRowSide showIndicator />
          </ListRowButton>
        </div>
      </section>
      <div className='flex items-center justify-between py-4'>
        <div className='flex items-center gap-2'>
          <Link href='/privacy-policy' className='text-secondary text-xs'>
            Privacy Policy
          </Link>
          {/* Don't have ToS yet */}
          {/* <span className='text-secondary text-xs'>·</span>
          <Link href='/' className='text-secondary text-xs'>
            Terms of service
          </Link> */}
        </div>
        {/* Hiding until we have actual versioning to show here */}
        {/* <span className='text-tertiary-100 text-xs'>Version 1.0.0 beta</span> */}
        {sessionId && (
          <span
            className='text-secondary text-xs cursor-pointer underline hover:text-link-hover'
            onClick={() => copyToClipboard(sessionId)}
          >
            {isCopied
              ? 'Copied to clipboard'
              : `Session ID: ${shorten(sessionId)}`}
          </span>
        )}
      </div>
    </motion.div>
  )
}
